/**
 * Which source file a light-preview stack frame points into.
 *
 * The light preview runs the whole app as one inline module inside a blob-URL document
 * (`createPreviewDocumentUrl`), so a crash's stack reads `at blob:https://…/<uuid>:1773:1`: a
 * line of that document, not of any file. "Fix with AI" sent that to the model, which had no
 * file to open and asked the founder which screen was blank (prototyper test finding 5).
 *
 * esbuild's unminified bundle puts a `// preview:src/App.tsx` comment before each module's code, so the
 * nearest such comment above the frame's line names the file. The documents of the pages the
 * preview served recently are kept here, keyed by their blob URL, to read that from.
 */

const MAX_DOCUMENTS = 3;
const documents = new Map<string, string[]>();

/** Called when the preview publishes a document; the oldest beyond a few is dropped. */
export function rememberPreviewDocument(url: string, html: string): void {
  documents.set(url, html.split('\n'));
  while (documents.size > MAX_DOCUMENTS) {
    const oldest = documents.keys().next().value as string;
    documents.delete(oldest);
  }
}

export function forgetPreviewDocument(url: string): void {
  documents.delete(url);
}

/**
 * A frame like `blob:https://host/uuid:1773:12`, with or without surrounding parentheses. Lazy,
 * so a host with a port (`blob:http://localhost:3000/uuid:8:10`) still ends at the last pair.
 */
const BLOB_FRAME = /(blob:[^\s()]+?):(\d+):(\d+)(?=[\s)]|$)/;
/**
 * esbuild's module marker on a line of its own. The preview's resolver puts files in the
 * `preview` namespace, so it reads `// preview:src/pages/Home.tsx`.
 */
const MODULE_MARKER = /^\s*\/\/\s+(?:[\w-]+:)?((?:src|app)\/[^\s]+\.[cm]?[jt]sx?)\s*$/;

/**
 * The workspace file of the first blob frame in `stack`, e.g. `src/App.tsx`; undefined when the
 * stack has no blob frame or its document is no longer known.
 */
export function sourceFileForPreviewStack(stack: string | undefined): string | undefined {
  if (!stack) return undefined;
  for (const raw of stack.split('\n')) {
    const match = BLOB_FRAME.exec(raw);
    if (!match) continue;
    const lines = documents.get(match[1]);
    if (!lines) continue;
    const lineIndex = Number(match[2]) - 1;
    for (let i = Math.min(lineIndex, lines.length - 1); i >= 0; i -= 1) {
      const marker = MODULE_MARKER.exec(lines[i]);
      if (marker) return marker[1];
    }
  }
  return undefined;
}

/** Test hook. */
export function clearPreviewDocuments(): void {
  documents.clear();
}
